import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { weekDays } from './constants';
import type { CalendarDay } from './types';
import { isSameDay } from './utils';

type StudentOption = {
  id: string;
  name: string;
};

type SchedulePanelProps = {
  data: {
    isOpen: boolean;
    monthLabel: string;
    calendarDays: CalendarDay[];
    selectedDate: Date;
    selectedTime: string;
    duration: number;
    studentId: string;
    studentOptions: StudentOption[];
    meetingPlatform: string;
    timezone: string;
    isStudentsLoading: boolean;
    canSchedule: boolean;
    isScheduling: boolean;
    scheduleError: boolean;
    scheduleErrorMessage: string;
  };
  actions: {
    onClose: () => void;
    onPrevMonth: () => void;
    onNextMonth: () => void;
    onDateSelect: (day: CalendarDay) => void;
    onTimeChange: (value: string) => void;
    onIncreaseDuration: () => void;
    onDecreaseDuration: () => void;
    onStudentChange: (value: string) => void;
    onMeetingPlatformChange: (value: string) => void;
    onTimezoneChange: (value: string) => void;
    onSchedule: () => void;
    onReset: () => void;
  };
};

const SchedulePanel = ({ data, actions }: SchedulePanelProps) => {
  if (!data.isOpen) {
    return null;
  }

  const selectedLabel = data.selectedDate.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <aside className="flex w-full flex-col gap-4 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm xl:w-[320px] xl:min-w-[320px] h-full overflow-hidden">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Schedule Session</p>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
          aria-label="Close"
          onClick={actions.onClose}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 overflow-y-auto pr-1 flex-1 custom-scrollbar">
        <p className="text-sm font-semibold text-slate-700">Select Date</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-100 text-slate-600"
            aria-label="Previous month"
            onClick={actions.onPrevMonth}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-slate-900">
            {data.monthLabel}
          </span>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-100 text-slate-600"
            aria-label="Next month"
            onClick={actions.onNextMonth}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-slate-400">
          {weekDays.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2 text-center text-sm">
          {data.calendarDays.map((day) => {
            const isSelected = isSameDay(day.date, data.selectedDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPast = day.date < today;
            return (
              <button
                key={day.date.toISOString()}
                type="button"
                onClick={() => actions.onDateSelect(day)}
                disabled={isPast}
                className={`flex h-9 w-9 items-center justify-center rounded-full transition ${
                  isPast
                    ? 'cursor-not-allowed text-slate-300 opacity-50'
                    : isSelected
                      ? 'bg-blue-600 text-white'
                      : day.inCurrentMonth
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-slate-300'
                }`}
              >
                {day.date.getDate()}
              </button>
            );
          })}
        </div>

        <div className="pt-3">
          <p className="text-sm font-semibold text-slate-900 mb-2">{selectedLabel}</p>
        
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-2">Time</p>
              <TextField
                select
                value={data.selectedTime}
                size="small"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected) {
                      const time = String(selected);
                      const [hours, minutes] = time.split(':');
                      const hour = parseInt(hours);
                      const period = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour > 12 ? hour - 12 : hour;
                      return `${displayHour.toString().padStart(2, '0')}:${minutes} ${period}`;
                    }
                    return <span style={{ color: '#94A3B8' }}>Select time</span>;
                  },
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                        marginTop: '8px',
                        maxHeight: '300px',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                  },
                }}
                onChange={(event) => {
                  const selectedTime = event.target.value;
                  console.log('🕐 [TIME PICKER] User selected time:', {
                    rawValue: selectedTime,
                    displayValue: (() => {
                      const [hours, minutes] = selectedTime.split(':');
                      const hour = parseInt(hours);
                      const period = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                      return `${displayHour.toString().padStart(2, '0')}:${minutes} ${period}`;
                    })(),
                  });
                  actions.onTimeChange(selectedTime);
                }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    height: '40px',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: data.selectedTime ? '#475569' : '#94A3B8',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563EB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              >
                <MenuItem value="09:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>09:00 AM</MenuItem>
                <MenuItem value="09:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>09:15 AM</MenuItem>
                <MenuItem value="09:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>09:30 AM</MenuItem>
                <MenuItem value="09:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>09:45 AM</MenuItem>
                <MenuItem value="10:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>10:00 AM</MenuItem>
                <MenuItem value="10:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>10:15 AM</MenuItem>
                <MenuItem value="10:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>10:30 AM</MenuItem>
                <MenuItem value="10:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>10:45 AM</MenuItem>
                <MenuItem value="11:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>11:00 AM</MenuItem>
                <MenuItem value="11:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>11:15 AM</MenuItem>
                <MenuItem value="11:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>11:30 AM</MenuItem>
                <MenuItem value="11:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>11:45 AM</MenuItem>
                <MenuItem value="12:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>12:00 PM</MenuItem>
                <MenuItem value="12:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>12:15 PM</MenuItem>
                <MenuItem value="12:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>12:30 PM</MenuItem>
                <MenuItem value="12:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>12:45 PM</MenuItem>
                <MenuItem value="13:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>01:00 PM</MenuItem>
                <MenuItem value="13:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>01:15 PM</MenuItem>
                <MenuItem value="13:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>01:30 PM</MenuItem>
                <MenuItem value="13:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>01:45 PM</MenuItem>
                <MenuItem value="14:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>02:00 PM</MenuItem>
                <MenuItem value="14:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>02:15 PM</MenuItem>
                <MenuItem value="14:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>02:30 PM</MenuItem>
                <MenuItem value="14:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>02:45 PM</MenuItem>
                <MenuItem value="15:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>03:00 PM</MenuItem>
                <MenuItem value="15:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>03:15 PM</MenuItem>
                <MenuItem value="15:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>03:30 PM</MenuItem>
                <MenuItem value="15:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>03:45 PM</MenuItem>
                <MenuItem value="16:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>04:00 PM</MenuItem>
                <MenuItem value="16:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>04:15 PM</MenuItem>
                <MenuItem value="16:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>04:30 PM</MenuItem>
                <MenuItem value="16:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>04:45 PM</MenuItem>
                <MenuItem value="17:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>05:00 PM</MenuItem>
                <MenuItem value="17:15" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>05:15 PM</MenuItem>
                <MenuItem value="17:30" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>05:30 PM</MenuItem>
                <MenuItem value="17:45" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>05:45 PM</MenuItem>
                <MenuItem value="18:00" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: 'none' }}>06:00 PM</MenuItem>
              </TextField>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-2">Timezone</p>
              <TextField
                select
                value={data.timezone}
                size="small"
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                        marginTop: '8px',
                        maxHeight: '300px',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                  },
                }}
                onChange={(event) => actions.onTimezoneChange(event.target.value)}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    height: '40px',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#475569',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563EB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              >
                <MenuItem value="America/New_York" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Eastern Time</MenuItem>
                <MenuItem value="America/Chicago" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Central Time</MenuItem>
                <MenuItem value="America/Denver" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Mountain Time</MenuItem>
                <MenuItem value="America/Los_Angeles" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Pacific Time</MenuItem>
                <MenuItem value="America/Anchorage" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Alaska Time</MenuItem>
                <MenuItem value="Pacific/Honolulu" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Hawaii Time</MenuItem>
                <MenuItem value="Europe/London" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>London (GMT)</MenuItem>
                <MenuItem value="Europe/Paris" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Paris (CET)</MenuItem>
                <MenuItem value="Asia/Dubai" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Dubai (GST)</MenuItem>
                <MenuItem value="Asia/Kolkata" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>India (IST)</MenuItem>
                <MenuItem value="Asia/Singapore" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Singapore</MenuItem>
                <MenuItem value="Asia/Tokyo" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>Tokyo (JST)</MenuItem>
                <MenuItem value="Australia/Sydney" sx={{ padding: '12px 16px', fontSize: '13px', borderBottom: 'none' }}>Sydney (AEST)</MenuItem>
              </TextField>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-2">Duration</p>
              <TextField
                select
                value={data.duration}
                size="small"
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                        marginTop: '8px',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                  },
                }}
                onChange={(event) => {
                  const newDuration = Number(event.target.value);
                  const diff = newDuration - data.duration;
                  if (diff > 0) {
                    for (let i = 0; i < diff / 15; i++) {
                      actions.onIncreaseDuration();
                    }
                  } else if (diff < 0) {
                    for (let i = 0; i < Math.abs(diff) / 15; i++) {
                      actions.onDecreaseDuration();
                    }
                  }
                }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    height: '40px',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#475569',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563EB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              >
                <MenuItem value={30} sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>30 minutes</MenuItem>
                <MenuItem value={60} sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>60 minutes</MenuItem>
                <MenuItem value={90} sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>90 minutes</MenuItem>
                <MenuItem value={120} sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>120 minutes</MenuItem>
                <MenuItem value={150} sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: 'none' }}>150 minutes</MenuItem>
              </TextField>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-2">
                Meeting platform
              </p>
              <TextField
                select
                value={data.meetingPlatform}
                size="small"
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                        marginTop: '8px',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                  },
                }}
                onChange={(event) =>
                  actions.onMeetingPlatformChange(event.target.value)
                }
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    height: '40px',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#475569',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563EB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              >
                <MenuItem value="in-house" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>In-house</MenuItem>
                <MenuItem value="zoom" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>Zoom</MenuItem>
                <MenuItem value="google-meet" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #F1F5F9' }}>Google Meet</MenuItem>
                <MenuItem value="microsoft-teams" sx={{ padding: '12px 16px', fontSize: '14px', borderBottom: 'none' }}>Microsoft Teams</MenuItem>
              </TextField>
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm font-semibold text-slate-900">Student</p>
            {data.isStudentsLoading ? (
              <div className="mt-2 h-10 w-full rounded-lg bg-slate-100" />
            ) : (
              <TextField
                select
                value={data.studentId}
                size="small"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected ? (
                      data.studentOptions.find((s) => s.id === selected)?.name || 'Select student'
                    ) : (
                      <span style={{ color: '#94A3B8' }}>Select student</span>
                    ),
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                        marginTop: '8px',
                        maxHeight: '300px',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                  },
                }}
                onChange={(event) => actions.onStudentChange(event.target.value)}
                sx={{
                  marginTop: '8px',
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    height: '40px',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: data.studentId ? '#475569' : '#94A3B8',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563EB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              >
                {data.studentOptions.length === 0 ? (
                  <MenuItem value="" disabled sx={{ padding: '12px 16px', fontSize: '14px', color: '#94A3B8' }}>
                    No students found
                  </MenuItem>
                ) : (
                  data.studentOptions.map((student, index) => (
                    <MenuItem 
                      key={student.id} 
                      value={student.id}
                      sx={{ 
                        padding: '12px 16px', 
                        fontSize: '14px',
                        borderBottom: index < data.studentOptions.length - 1 ? '1px solid #F1F5F9' : 'none'
                      }}
                    >
                      {student.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white transition ${
                data.canSchedule && !data.isScheduling
                  ? 'bg-slate-900 hover:bg-slate-800'
                  : 'cursor-not-allowed bg-slate-300'
              }`}
              onClick={actions.onSchedule}
              disabled={!data.canSchedule || data.isScheduling}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M8 2v4M16 2v4" />
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M3 10h18" />
                <path d="M8 14h8" />
              </svg>
              {data.isScheduling ? 'Scheduling...' : 'Schedule'}
            </button>
            <button
              type="button"
              className="flex flex-1 items-center justify-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
              onClick={actions.onReset}
            >
              Reset
            </button>
          </div>
          {data.scheduleError ? (
            <p className="text-xs text-rose-500">
              {data.scheduleErrorMessage}
            </p>
          ) : null}
        </div>
      </div>
    </aside>
  );
};

export default SchedulePanel;
